import type { ReactNode } from 'react'
import { Inbox } from 'lucide-react'
import { Link } from '../../../router.js'
import { fmtDateHeure } from '../../api.js'
import { fmtAge, nomSalle, Pastille, PastilleEtat, useVueSonde, type Ton } from '../commun.js'
import { cheminAppareil, tonSource, type PointAttention, type ResumeAppareil, type SourceDoc } from './reseau-model.js'
import { useMenuSonde } from '../menu-sonde.js'
import './reseau.css'

/**
 * Briques d'affichage des écrans Réseau et Serveur (21/10/2026) : une
 * lecture de Sonde avec sa dernière donnée gardée (grise pendant une panne),
 * une section, une tuile chiffrée, une jauge, un état vide qui dit pourquoi.
 * Couleurs : le code unique de `commun.tsx` (ok, warn, err, off, maint, gris).
 */

/** Lecture d'une vue : `data` = dernière donnée réussie ; `perime` = la lecture en cours a échoué. */
export function useDoc<T>(chemin: string | null, garde: (x: unknown) => x is T, intervalMs?: number) {
  const { lecture, precedente, recharger } = useVueSonde(chemin, garde, intervalMs)
  const data = lecture?.etat === 'ok' ? lecture.data : precedente
  return { lecture, data, perime: lecture !== null && lecture.etat !== 'ok', charge: lecture === null, recharger }
}

export function Section(props: {
  id: string
  titre: string
  icone?: ReactNode
  droite?: ReactNode
  perime?: boolean
  children: ReactNode
}) {
  return (
    <section className="lc-panel sv-res-section" aria-labelledby={props.id}>
      <header className="lc-panel__head sv-res-section__tete">
        <h2 className="lc-section-title sv-res-section__titre" id={props.id}>{props.icone}{props.titre}</h2>
        {props.droite ? <div className="sv-res-section__droite">{props.droite}</div> : null}
      </header>
      <div className={`lc-panel__body sv-res-section__corps${props.perime ? ' sv-res-perime' : ''}`}>{props.children}</div>
    </section>
  )
}

/** Les sources de Sonde derrière une section : indisponible = gris, jamais rouge. */
export function Sources({ sources }: { sources: SourceDoc[] | null | undefined }) {
  if (!sources || sources.length === 0) return null
  return (
    <span className="sv-res-sources">
      {sources.map((s) => (
        <Pastille key={s.code} ton={tonSource(s.status)}
          title={`${s.label} : ${s.status_label}${s.since ? ` depuis le ${fmtDateHeure(s.since)}` : ''}${s.last_error_label ? ` — ${s.last_error_label}` : ''}`}>
          {s.label}
        </Pastille>
      ))}
    </span>
  )
}

/** Âge d'une mesure, avec l'heure exacte au survol. */
export function Age({ s, at, prefixe }: { s: number | null | undefined; at?: string | null; prefixe?: string }) {
  return (
    <span className="sv-res-age" title={at ? `Relevé le ${fmtDateHeure(at)}` : undefined}>
      {prefixe ? `${prefixe} ` : ''}{fmtAge(s)}
    </span>
  )
}

/** Tuile chiffrée du bandeau : la couleur dit l'état (`direct` = cyan, un simple compte en direct), le détail dit l'âge et la source. */
export function Tuile(props: { ton: Ton | 'direct'; label: string; valeur: ReactNode; detail?: ReactNode; href?: string; icone?: ReactNode }) {
  const corps = (
    <>
      <span className="sv-res-tuile__label">{props.icone}{props.label}</span>
      <span className="sv-res-tuile__valeur">{props.valeur}</span>
      {props.detail ? <span className="sv-res-tuile__detail">{props.detail}</span> : null}
    </>
  )
  const cls = `sv-res-tuile sv-res-tuile--${props.ton}`
  return props.href ? <Link className={`${cls} sv-res-tuile--lien`} href={props.href}>{corps}</Link> : <div className={cls}>{corps}</div>
}

/** Jauge 0-100 : couleur du niveau ; valeur absente = piste vide grise (jamais 0 %). */
export function Jauge({ pct, ton, label }: { pct: number | null | undefined; ton: Ton; label: string }) {
  const v = pct === null || pct === undefined || !Number.isFinite(pct) ? null : Math.max(0, Math.min(100, pct))
  return (
    <span className={`sv-res-jauge sv-res-jauge--${v === null ? 'gris' : ton}`} role="img" aria-label={`${label} : ${v === null ? 'inconnu' : `${Math.round(v)} %`}`}>
      {v === null ? null : <span className="sv-res-jauge__plein" style={{ width: `${v}%` }} />}
    </span>
  )
}

/** État vide : ce qui manque, pourquoi, et où agir. */
export function Vide({ titre, texte, action, icone }: { titre: string; texte?: ReactNode; action?: ReactNode; icone?: ReactNode }) {
  return (
    <div className="sv-res-vide">
      <span className="sv-res-vide__icone" aria-hidden="true">{icone ?? <Inbox size={20} />}</span>
      <div className="sv-res-vide__corps">
        <p className="sv-res-vide__titre">{titre}</p>
        {texte ? <p className="sv-res-vide__texte">{texte}</p> : null}
        {action ? <div className="sv-res-vide__action">{action}</div> : null}
      </div>
    </div>
  )
}

/** Nom d'un appareil, cliquable vers sa fiche. */
export function LienAppareil({ uid, nom, zone }: { uid: string; nom: string; zone?: string | null }) {
  const menu = useMenuSonde()
  return <Link className="sv-res-lien" href={cheminAppareil(uid)} {...menu.appareil({ uid, nom, zone_code: zone ?? null })}>{nom}</Link>
}

/** En-tête d'une carte d'appareil : nom, salle, état de Sonde (avec « depuis »). */
export function TeteAppareil({ d, details, extra }: { d: ResumeAppareil; details: boolean; extra?: ReactNode }) {
  const s = d.state
  return (
    <div className="sv-res-carte__tete">
      <div className="sv-res-carte__nom">
        <LienAppareil uid={d.uid} nom={d.display_name} zone={d.zone_code} />
        <span className="sv-res-carte__meta">
          {d.zone_code ? <span className="sv-res-zone">{nomSalle(d.zone_code)}</span> : null}
          {details ? <span className="sv-mono">{d.uid}</span> : null}
          {extra}
        </span>
      </div>
      <span title={[s.cause_label, s.since ? `depuis le ${fmtDateHeure(s.since)}` : null, s.exception?.reason ?? null].filter(Boolean).join(' · ') || undefined}>
        <PastilleEtat etat={s.display_state} libelle={s.display_label} />
      </span>
    </div>
  )
}

/** Liste d'attention : ce qui demande un regard, le plus grave d'abord. */
export function ListeAttention({ points, max = 6, ouvert, basculer }: { points: PointAttention[]; max?: number; ouvert: boolean; basculer: () => void }) {
  if (points.length === 0) return null
  const visibles = ouvert ? points : points.slice(0, max)
  return (
    <div className="sv-res-attention">
      <ul className="sv-res-attention__liste">
        {visibles.map((p, i) => (
          <li key={`${p.texte}-${i}`} className={`sv-res-attention__ligne sv-res-attention__ligne--${p.ton}`}>
            <span className="sv-res-point" aria-hidden="true" />
            {p.href ? <Link className="sv-res-lien" href={p.href}>{p.texte}</Link> : <span>{p.texte}</span>}
          </li>
        ))}
      </ul>
      {points.length > max ? (
        <button type="button" className="lc-btn lc-btn--ghost sv-res-plus" onClick={basculer} aria-expanded={ouvert}>
          {ouvert ? 'Réduire' : `Voir les ${points.length - max} autres`}
        </button>
      ) : null}
    </div>
  )
}
